// Costeo teórico vs real por orden de producción: compara lo que la ficha técnica
// dice que debió consumirse (ajustado a las porciones REALES que rindió cada batch)
// contra lo que efectivamente salió de bodega, y permite registrar ajustes de
// consumo (entrega adicional o devolución) que mueven el kardex. Es el instrumento
// para calibrar fichas y cazar merma. Catering → Costeo → Costeo real.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Scale, TrendingUp, TrendingDown, Plus, Minus } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { fieldCls, num } from '@/features/inventario/shared';
import { catRpc, invalidateCatering, money } from './shared';
import { LOCALE } from '@/lib/utils/regional';

interface ResumenTotal { ordenes: number; costo_real: number; costo_teorico: number; desviacion_usd: number; desviacion_pct: number | null }
interface PorMaterial { material: string; unidad: string; teorico: number; real: number; desviacion: number; desviacion_pct: number | null; desviacion_usd: number }
interface PorOrden {
  orden_id: string; fecha: string; codigo: string; porciones_plan: number; porciones_reales: number | null;
  rendimiento_pct: number | null; costo_teorico: number; costo_real: number; desviacion_usd: number;
}
interface Resumen { resumen: ResumenTotal; por_material: PorMaterial[]; por_orden: PorOrden[] }

interface Batch { receta: string; tipo: string; porciones_plan: number; porciones_reales: number | null; rendimiento_pct: number | null }
interface Ajuste { material: string; cantidad: number; motivo: string; comentario: string | null; usuario: string | null; at: string }
interface Material {
  material_id: string; material: string; unidad: string; plan: number; teorico_ajustado: number; real: number;
  desviacion: number; desviacion_pct: number | null; costo_unit: number; desviacion_usd: number;
}
interface Desviacion {
  orden_id: string; codigo: string; fecha: string; estado: string; porciones_plan: number; porciones_reales: number | null;
  costo_mp_real: number; costo_mp_teorico: number; desviacion_usd: number; desviacion_pct: number | null;
  batches: Batch[]; ajustes: Ajuste[]; materiales: Material[];
}

const MOTIVOS = [
  { v: 'falto_producto', t: 'Faltó producto (entrega adicional)' },
  { v: 'porcion_extra', t: 'Porción extra / repitencia' },
  { v: 'accidente', t: 'Accidente / desperdicio' },
  { v: 'calidad', t: 'Descarte por calidad' },
  { v: 'devolucion', t: 'Devolución a bodega' },
  { v: 'otro', t: 'Otro' },
];
const primerDiaMes = (): string => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
const isoHoy = (): string => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const desvCls = (v: number | null | undefined) =>
  !v ? 'text-muted-foreground' : v > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400';

export function CosteoCatering() {
  const qc = useQueryClient();
  const [desde, setDesde] = useState(primerDiaMes());
  const [hasta, setHasta] = useState(isoHoy());
  const [selId, setSelId] = useState('');

  const { data: resumen } = useQuery<Resumen>({
    queryKey: ['cat-costeo-resumen', desde, hasta],
    queryFn: () => catRpc<Resumen>('cat_costeo_resumen', { _desde: desde, _hasta: hasta }),
  });
  const { data: det } = useQuery<Desviacion>({
    queryKey: ['cat-costeo-desviacion', selId], enabled: !!selId,
    queryFn: () => catRpc<Desviacion>('cat_costeo_desviacion', { _orden_id: selId }),
  });

  // Ajuste de consumo
  const [aj, setAj] = useState<{ material_id: string; cantidad: string; signo: 1 | -1; motivo: string; comentario: string }>(
    { material_id: '', cantidad: '', signo: 1, motivo: 'falto_producto', comentario: '' });
  const ajustar = useMutation({
    mutationFn: () => catRpc('cat_consumo_ajustar', {
      _orden_id: selId,
      _lineas: [{ material_id: aj.material_id, cantidad: aj.signo * Math.abs(parseFloat(aj.cantidad)) }],
      _motivo: aj.motivo, _comentario: aj.comentario || null,
    }),
    onSuccess: () => {
      toast.success('Consumo ajustado');
      setAj({ material_id: '', cantidad: '', signo: 1, motivo: 'falto_producto', comentario: '' });
      invalidateCatering(qc);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const r = resumen?.resumen;
  const ordsMasDesviadas = useMemo(
    () => [...(resumen?.por_orden ?? [])].sort((a, b) => Math.abs(b.desviacion_usd) - Math.abs(a.desviacion_usd)),
    [resumen]);

  return (
    <div className="space-y-4 p-4">
      {/* Filtros + KPIs */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base"><Scale className="h-4 w-4" /> Costeo teórico vs real</CardTitle>
          <div className="flex flex-wrap items-end gap-2 pt-1">
            <div><label className="block text-xs text-muted-foreground">Desde</label>
              <input type="date" className={`${fieldCls} w-40`} value={desde} onChange={(e) => setDesde(e.target.value)} /></div>
            <div><label className="block text-xs text-muted-foreground">Hasta</label>
              <input type="date" className={`${fieldCls} w-40`} value={hasta} onChange={(e) => setHasta(e.target.value)} /></div>
          </div>
        </CardHeader>
        <CardContent>
          {r && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-md border p-3"><p className="text-xs text-muted-foreground">Órdenes</p><p className="text-xl font-semibold">{r.ordenes}</p></div>
              <div className="rounded-md border p-3"><p className="text-xs text-muted-foreground">Costo teórico</p><p className="text-xl font-semibold font-mono">{money(r.costo_teorico, 2)}</p></div>
              <div className="rounded-md border p-3"><p className="text-xs text-muted-foreground">Costo real</p><p className="text-xl font-semibold font-mono">{money(r.costo_real, 2)}</p></div>
              <div className="rounded-md border p-3">
                <p className="text-xs text-muted-foreground">Desviación</p>
                <p className={`flex items-center gap-1 text-xl font-semibold font-mono ${desvCls(r.desviacion_usd)}`}>
                  {r.desviacion_usd > 0 ? <TrendingUp className="h-4 w-4" /> : r.desviacion_usd < 0 ? <TrendingDown className="h-4 w-4" /> : null}
                  {money(r.desviacion_usd, 2)}
                </p>
                <p className={`text-xs ${desvCls(r.desviacion_usd)}`}>{r.desviacion_pct != null ? `${r.desviacion_pct}%` : '—'} vs teórico</p>
              </div>
            </div>
          )}
          <p className="pt-2 text-xs text-muted-foreground">
            Positivo = se consumió <strong>más</strong> de lo que la ficha indica (merma, sobreporción o ficha subvaluada);
            negativo = se consumió menos (sobra o ficha inflada). El teórico se ajusta a las porciones reales de cada batch.
          </p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Por material */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Por materia prima (dónde se pierde)</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-1 pr-2">Material</th><th className="py-1 pr-2 text-right">Teórico</th>
                <th className="py-1 pr-2 text-right">Real</th><th className="py-1 pr-2 text-right">%</th>
                <th className="py-1 pr-2 text-right">Impacto</th>
              </tr></thead>
              <tbody>
                {(resumen?.por_material ?? []).map((m) => (
                  <tr key={m.material} className="border-b last:border-0">
                    <td className="py-1.5 pr-2">{m.material} <span className="text-xs text-muted-foreground">({m.unidad})</span></td>
                    <td className="py-1.5 pr-2 text-right font-mono">{num(m.teorico, 2)}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">{num(m.real, 2)}</td>
                    <td className={`py-1.5 pr-2 text-right font-mono ${desvCls(m.desviacion_pct)}`}>{m.desviacion_pct != null ? `${m.desviacion_pct}%` : '—'}</td>
                    <td className={`py-1.5 pr-2 text-right font-mono ${desvCls(m.desviacion_usd)}`}>{money(m.desviacion_usd, 2)}</td>
                  </tr>
                ))}
                {!resumen?.por_material?.length && <tr><td colSpan={5} className="py-2 text-sm text-muted-foreground">Sin datos en el período.</td></tr>}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Por orden */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Por orden (qué días se desviaron)</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-1 pr-2">Fecha</th><th className="py-1 pr-2">Orden</th>
                <th className="py-1 pr-2 text-right">Rend.</th><th className="py-1 pr-2 text-right">Teórico</th>
                <th className="py-1 pr-2 text-right">Real</th><th className="py-1 pr-2 text-right">Desv.</th>
              </tr></thead>
              <tbody>
                {ordsMasDesviadas.map((o) => (
                  <tr key={o.orden_id} onClick={() => setSelId(o.orden_id)}
                    className={`cursor-pointer border-b last:border-0 hover:bg-accent ${selId === o.orden_id ? 'bg-accent' : ''}`}>
                    <td className="py-1.5 pr-2 text-xs">{o.fecha}</td>
                    <td className="py-1.5 pr-2 font-medium">{o.codigo}</td>
                    <td className={`py-1.5 pr-2 text-right font-mono ${o.rendimiento_pct != null && o.rendimiento_pct < 0 ? 'text-amber-600' : ''}`}>
                      {o.rendimiento_pct != null ? `${o.rendimiento_pct}%` : '—'}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">{money(o.costo_teorico, 2)}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">{money(o.costo_real, 2)}</td>
                    <td className={`py-1.5 pr-2 text-right font-mono ${desvCls(o.desviacion_usd)}`}>{money(o.desviacion_usd, 2)}</td>
                  </tr>
                ))}
                {!ordsMasDesviadas.length && <tr><td colSpan={6} className="py-2 text-sm text-muted-foreground">Sin órdenes en el período.</td></tr>}
              </tbody>
            </table>
            <p className="pt-2 text-xs text-muted-foreground">Clic en una orden para ver su detalle y ajustar consumo.</p>
          </CardContent>
        </Card>
      </div>

      {/* Detalle de la orden seleccionada */}
      {det && (
        <Card className="border-primary">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{det.codigo} · {det.fecha}</CardTitle>
            <p className="pt-1 text-xs text-muted-foreground">
              Porciones plan {num(det.porciones_plan, 0)} → reales {det.porciones_reales != null ? num(det.porciones_reales, 0) : '—'} ·
              Real <span className="font-mono">{money(det.costo_mp_real, 2)}</span> vs Teórico <span className="font-mono">{money(det.costo_mp_teorico, 2)}</span> ·
              Desviación <span className={`font-mono ${desvCls(det.desviacion_usd)}`}>{money(det.desviacion_usd, 2)} ({det.desviacion_pct != null ? `${det.desviacion_pct}%` : '—'})</span>
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Rendimiento por batch */}
            <div>
              <p className="pb-1 text-sm font-medium">Rendimiento por batch</p>
              <div className="flex flex-wrap gap-2">
                {det.batches.map((b) => (
                  <span key={b.receta} className={`rounded-md border px-2 py-1 text-xs ${b.rendimiento_pct != null && b.rendimiento_pct < 100 ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20' : ''}`}>
                    {b.receta}: {num(b.porciones_plan, 0)}→{b.porciones_reales != null ? num(b.porciones_reales, 0) : '—'}
                    {b.rendimiento_pct != null && <strong className="ml-1">{b.rendimiento_pct}%</strong>}
                  </span>
                ))}
              </div>
            </div>

            {/* Materiales */}
            <div className="overflow-x-auto">
              <p className="pb-1 text-sm font-medium">Materiales</p>
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-1 pr-2">Material</th><th className="py-1 pr-2 text-right">Teórico aj.</th>
                  <th className="py-1 pr-2 text-right">Real</th><th className="py-1 pr-2 text-right">Desv.</th>
                  <th className="py-1 pr-2 text-right">%</th><th className="py-1 pr-2 text-right">Impacto</th>
                </tr></thead>
                <tbody>
                  {det.materiales.map((m) => (
                    <tr key={m.material_id} className="border-b last:border-0">
                      <td className="py-1.5 pr-2">{m.material} <span className="text-xs text-muted-foreground">({m.unidad})</span></td>
                      <td className="py-1.5 pr-2 text-right font-mono">{num(m.teorico_ajustado, 3)}</td>
                      <td className="py-1.5 pr-2 text-right font-mono">{num(m.real, 3)}</td>
                      <td className={`py-1.5 pr-2 text-right font-mono ${desvCls(m.desviacion)}`}>{num(m.desviacion, 3)}</td>
                      <td className={`py-1.5 pr-2 text-right font-mono ${desvCls(m.desviacion_pct)}`}>{m.desviacion_pct != null ? `${m.desviacion_pct}%` : '—'}</td>
                      <td className={`py-1.5 pr-2 text-right font-mono ${desvCls(m.desviacion_usd)}`}>{money(m.desviacion_usd, 2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Ajustar consumo */}
            {det.estado !== 'anulada' && (
              <div className="rounded-md border p-3">
                <p className="pb-2 text-sm font-medium">Registrar ajuste de consumo real</p>
                <div className="grid gap-2 sm:grid-cols-6">
                  <select className={`${fieldCls} sm:col-span-2`} value={aj.material_id} onChange={(e) => setAj({ ...aj, material_id: e.target.value })}>
                    <option value="">Material…</option>
                    {det.materiales.map((m) => <option key={m.material_id} value={m.material_id}>{m.material}</option>)}
                  </select>
                  <div className="flex items-center gap-1">
                    <button type="button" title="Consumo adicional" onClick={() => setAj({ ...aj, signo: 1 })}
                      className={`rounded-md border p-2 ${aj.signo === 1 ? 'border-red-400 bg-red-50 dark:bg-red-900/20' : ''}`}><Plus className="h-4 w-4" /></button>
                    <button type="button" title="Devolución a bodega" onClick={() => setAj({ ...aj, signo: -1 })}
                      className={`rounded-md border p-2 ${aj.signo === -1 ? 'border-green-400 bg-green-50 dark:bg-green-900/20' : ''}`}><Minus className="h-4 w-4" /></button>
                  </div>
                  <input className={fieldCls} type="number" step="0.001" placeholder="Cantidad" value={aj.cantidad} onChange={(e) => setAj({ ...aj, cantidad: e.target.value })} />
                  <select className={`${fieldCls} sm:col-span-2`} value={aj.motivo} onChange={(e) => setAj({ ...aj, motivo: e.target.value })}>
                    {MOTIVOS.map((m) => <option key={m.v} value={m.v}>{m.t}</option>)}
                  </select>
                  <input className={`${fieldCls} sm:col-span-5`} placeholder="Comentario (opcional)" value={aj.comentario} onChange={(e) => setAj({ ...aj, comentario: e.target.value })} />
                  <Button className="sm:col-span-1" disabled={!aj.material_id || !aj.cantidad || ajustar.isPending} onClick={() => ajustar.mutate()}>Aplicar</Button>
                </div>
                <p className="pt-1 text-[11px] text-muted-foreground">
                  «+» descuenta más producto de bodega (entrega adicional); «−» lo devuelve. El movimiento afecta el kardex y recalcula el costo real.
                </p>
              </div>
            )}

            {/* Historial de ajustes */}
            {det.ajustes.length > 0 && (
              <div>
                <p className="pb-1 text-sm font-medium">Ajustes registrados</p>
                <ul className="space-y-1 text-xs">
                  {det.ajustes.map((a, i) => (
                    <li key={i} className="flex flex-wrap items-center gap-2 rounded-md bg-muted/50 px-2 py-1">
                      <span className={`font-mono ${a.cantidad > 0 ? 'text-red-600' : 'text-green-600'}`}>{a.cantidad > 0 ? '+' : ''}{num(a.cantidad, 3)}</span>
                      <span className="font-medium">{a.material}</span>
                      <span className="rounded-full bg-background px-2 py-0.5">{MOTIVOS.find((m) => m.v === a.motivo)?.t ?? a.motivo}</span>
                      {a.comentario && <span className="italic text-muted-foreground">"{a.comentario}"</span>}
                      <span className="ml-auto text-muted-foreground">{a.usuario ?? '—'} · {new Date(a.at).toLocaleString(LOCALE)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
